/* ============================================================
   Judge.me cards carousel — dash pagination + horizontal wheel
   Standalone: only loaded by layout/theme.liquid alongside
   assets/judgeme-reviews.css.

   The widget ships side arrows and no dots. The CSS floats those
   arrows over the slider; this file adds a row of dashes underneath
   and trackpad / horizontal-wheel scrolling.

   Everything is driven through the app's own global functions —
   window.jdgmNextCard(blockId) / jdgmPreviousCard(blockId) — rather
   than by touching the track transform, so the app keeps ownership of
   the animation, the card cloning and the autoplay. One call to those
   moves exactly one review, which is what every control here does.
   ============================================================ */
(function () {
  'use strict';

  var STEP_MS = 90;    // gap between successive next/prev calls
  var SETTLE_MS = 700; // long enough for the app's slide to finish before re-reading
  var MAX_DOTS = 10;  // beyond this the dashes read as a dotted rule, not pagination

  function init(widget) {
    if (!widget || widget.dataset.chiaraNav === 'true') return;

    var blockId = widget.getAttribute('data-jdgm-block-id');
    var content = widget.querySelector('.jdgm-content');
    var wrapper = widget.querySelector('.jdgm-cards-wrapper');
    if (!blockId || !content || !wrapper) return;

    if (typeof window.jdgmNextCard !== 'function') return; // app not ready yet

    var cards = [].slice.call(widget.querySelectorAll('.jdgm-card'));
    var real = cards.filter(function (c) {
      return c.className.indexOf('clone') === -1;
    });
    if (!real.length) return;

    var visible = parseInt(getComputedStyle(widget).getPropertyValue('--visible-cards'), 10) || 1;
    if (real.length <= visible) return; // nothing to move through

    widget.dataset.chiaraNav = 'true';

    /* ---------- where are we ---------- */
    function currentIndex() {
      // the card whose left edge sits closest to the wrapper's left edge
      var edge = wrapper.getBoundingClientRect().left;
      var best = null;
      var bestGap = Infinity;

      cards.forEach(function (card) {
        var gap = Math.abs(card.getBoundingClientRect().left - edge);
        if (gap < bestGap) {
          bestGap = gap;
          best = card;
        }
      });

      return best ? parseInt(best.dataset.cardIndex, 10) || 0 : 0;
    }

    /* ---------- move ----------
       One call to the app's function slides by one review, but the slide
       is animated: read the position again too soon and it still reports
       the old card, which made an earlier version overshoot badly. So the
       number of steps is worked out once, fired on a timer, and only then
       is the result checked and corrected once. */
    var moving = false;

    function fire(steps, done) {
      var move = steps > 0 ? window.jdgmNextCard : window.jdgmPreviousCard;
      var left = Math.abs(steps);

      (function step() {
        if (!left) {
          window.setTimeout(done, SETTLE_MS);
          return;
        }
        move(blockId);
        left -= 1;
        window.setTimeout(step, STEP_MS);
      })();
    }

    function goToIndex(target) {
      if (moving) return;

      target = Math.max(0, Math.min(target, positions - 1));
      var steps = target - currentIndex();
      if (!steps) return;

      moving = true;

      fire(steps, function () {
        // one correction pass, in case a step was swallowed mid-animation
        var off = target - currentIndex();

        if (!off) {
          moving = false;
          paint();
          return;
        }

        fire(off, function () {
          moving = false;
          paint();
        });
      });
    }

    function nudge(steps) {
      if (moving || !steps) return;
      goToIndex(currentIndex() + steps);
    }

    /* ---------- dashes ---------- */
    // One dash per scroll position, so clicking the next dash advances a
    // single review rather than a page. The number of positions is
    // (reviews - visible + 1); keep the block's Max reviews setting low
    // enough that this stays a readable number of dashes.
    var positions = Math.max(1, real.length - visible + 1);
    var group = Math.ceil(positions / MAX_DOTS);   // only kicks in past MAX_DOTS
    var dotCount = Math.ceil(positions / group);

    var nav = document.createElement('div');
    nav.className = 'jdgm-chiara-dots';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Reviews');

    for (var i = 0; i < dotCount; i += 1) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'jdgm-chiara-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to review ' + (i * group + 1));
      dot.dataset.position = String(i);
      nav.appendChild(dot);
    }

    content.insertAdjacentElement('afterend', nav);
    var dots = [].slice.call(nav.children);

    function paint() {
      var here = Math.min(currentIndex(), positions - 1);
      var active = Math.min(dotCount - 1, Math.floor(here / group));
      dots.forEach(function (dot, index) {
        var on = index === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', String(on));
      });
    }

    nav.addEventListener('click', function (event) {
      var dot = event.target.closest('.jdgm-chiara-dot');
      if (!dot) return;
      goToIndex(parseInt(dot.dataset.position, 10) * group);
    });

    /* ---------- horizontal wheel / trackpad ---------- */
    var wheelLock = false;

    wrapper.addEventListener('wheel', function (event) {
      // vertical scrolling belongs to the page, so only act on sideways intent
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();

      if (wheelLock || Math.abs(event.deltaX) < 8) return;
      wheelLock = true;
      window.setTimeout(function () { wheelLock = false; }, 320);

      nudge(event.deltaX > 0 ? 1 : -1);
    }, { passive: false });

    // the arrows and the app's autoplay move it too, so keep the dashes in sync
    window.setInterval(paint, 400);
    paint();
  }

  function boot() {
    document.querySelectorAll('.jdgm-cards-carousel').forEach(init);
  }

  // the app renders its cards client-side, so wait for them to appear
  function watch() {
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      var ready = document.querySelector('.jdgm-cards-carousel .jdgm-card');
      if (ready || tries > 60) {
        window.clearInterval(timer);
        if (ready) boot();
      }
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
